import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, RouterLink, Router, RouterOutlet } from '@angular/router';
import { NgScrollbar } from 'ngx-scrollbar';

import { IconDirective } from '@coreui/icons-angular';
import {
    ContainerComponent,
    ShadowOnScrollDirective,
    SidebarBrandComponent,
    SidebarComponent,
    SidebarFooterComponent,
    SidebarHeaderComponent,
    SidebarNavComponent,
    SidebarToggleDirective,
    SidebarTogglerDirective
} from '@coreui/angular';

import Swal from 'sweetalert2';

import { DefaultFooterComponent, DefaultHeaderComponent } from './';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { ApiService } from 'src/app/services/api.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BudgetUserPermissionService } from '../../services/budget-user-permission.service';
import { PropertyUserPermissionService } from '../../services/property-user-permission.service';
import { ModulePermissionService } from '../../services/module-permission.service';
import { INavData } from '@coreui/angular';
import { forkJoin, Subscription, from } from 'rxjs';
import { BUDGET_MODULE_PERMISSION, MODULE_PERMISSION } from '../../constants';

function isOverflown(element: HTMLElement) {
    return (
        element.scrollHeight > element.clientHeight ||
        element.scrollWidth > element.clientWidth
    );
}

@Component({
    selector: 'app-dashboard',
    templateUrl: './default-layout.component.html',
    styleUrls: ['./default-layout.component.scss'],
    standalone: true,
    imports: [
        SidebarComponent,
        SidebarHeaderComponent,
        SidebarBrandComponent,
        RouterLink,
        IconDirective,
        NgScrollbar,
        SidebarNavComponent,
        SidebarFooterComponent,
        SidebarToggleDirective,
        SidebarTogglerDirective,
        DefaultHeaderComponent,
        ShadowOnScrollDirective,
        ContainerComponent,
        RouterOutlet,
        DefaultFooterComponent,
        TranslatePipe
    ]
})
export class DefaultLayoutComponent implements OnDestroy {
    public navItems: any = [];
    public buyerNavItems: INavData[] = [];
    public sellerNavItems: INavData[] = [];
    public buyerAdminNavItems: INavData[] = [];
    public sellerAdminNavItems: INavData[] = [];
    public superAdminNavItems: INavData[] = [];
    public isPersonal: boolean = false;
    public userName: string = '';
    public userInitials: string = '';
    public userRole: string = '';
    public userRoleKey: string = '';
    public badgeCountEprojectClients = 0;
    public badgeCountMessages = 0;
    public badgeCountCompanies = 0;
    public badgeCountCompanyUser = 0;
    public badgeCountPersonalUser = 0;
    public badgeCountMySeller = 0;
    public badgeCountMyBuyer = 0;
    public badgeCountProduct = 0;
    public badgeCountCostCode = 0;
    public badgeCountCostManager = 0;
    public badgeCountBudget = 0;
    public badgeCountPurchaseRequest = 0;
    public badgeCountRequestForQuotation = 0;
    public badgeCountInvitedRFQ = 0;
    public badgeCountPublicRFQ = 0;
    public badgeCountPurchaseOrder = 0;
    public badgeCountReceivedPO = 0;
    public badgeCountGoodsReceiptNote = 0;

    private previousUrl: string | null = null;
    private currentUrl: string | null = null;
    private routerSubscription?: Subscription;

    constructor(private auth: AuthenticationService, private api: ApiService, private router: Router, private translate: TranslateService, private modulePermissionService: ModulePermissionService, private budgetUserPermissionService: BudgetUserPermissionService, private propertyUserPermissionService: PropertyUserPermissionService) {
        // Subscribe to router events to track the current and previous URLs
        this.routerSubscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.previousUrl = this.currentUrl;
                this.currentUrl = event.urlAfterRedirects;
            }
        });
    }

    ngOnInit(): void {
        let user = this.auth.user();

        this.isPersonal = user.user_type?.includes('Personal');

        this.setUserSession(user);

        this.auth.getCurrentUserAccessToken(user.id).subscribe(
            (result) => {
                this.initializeNavItems(user);
            },
            (err) => {
                this.api.checkUserBlockStatus(user.id).subscribe(
                    (userBlockData: any) => {
                        if (userBlockData.is_active === false) {
                            Swal.fire({
                                icon: "error",
                                title: "Account Deleted",
                                text: "Your account has been deleted."
                            }).then((result) => {
                                localStorage.removeItem('user');
                                localStorage.removeItem('token');
                                this.auth.toggleLogin(false);

                                this.router.navigate(['/login']);
                            });
                        } else if (userBlockData.is_block) {
                            Swal.fire({
                                icon: "error",
                                title: "Account Blocked",
                                text: "Your account has been blocked, please contact your administrator."
                            }).then((result) => {
                                // clear local storage
                                localStorage.removeItem('user');
                                localStorage.removeItem('token');
                                this.auth.toggleLogin(false);

                                this.router.navigate(['/login']);
                            });
                        } else {
                            Swal.fire({
                                icon: "error",
                                title: "Oops...",
                                text: "Your session has expired, please login again."
                            }).then((result) => {
                                // clear local storage
                                localStorage.removeItem('user');
                                localStorage.removeItem('token');

                                this.router.navigate(['/login']);
                            });
                        }
                    },
                    (fetchErr) => {
                        Swal.fire({
                            icon: "error",
                            title: "Oops...",
                            text: "Your session has expired, please login again."
                        }).then((result) => {
                            // clear local storage
                            localStorage.removeItem('user');
                            localStorage.removeItem('token');

                            this.router.navigate(['/login']);
                        });
                    }
                );
            }
        );
    }

    languageChangedEvent(language: any) {
        this.translate.use(language);
        this.ngOnInit();
    }

    private setUserSession(user: any): void {
        const fullName =
            user?.name ||
            [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
            user?.email ||
            'User';

        this.userName = fullName;
        this.userInitials = this.getInitials(fullName);

        if (user?.is_super_admin) {
            this.userRole = 'Super Admin';
            this.userRoleKey = 'role.superAdmin';
        } else if (user?.is_company_admin) {
            this.userRole = 'Company Admin';
            this.userRoleKey = 'role.companyAdmin';
        } else {
            this.userRole = user?.user_type || 'User';
            this.userRoleKey = user?.user_type ? `userType.${user.user_type.toLowerCase()}` : 'role.user';
        }
    }

    private getInitials(name: string): string {
        if (!name) return '';
        const parts = name.trim().split(/\s+/);
        const first = parts[0]?.[0] ?? '';
        const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
        return (first + last).toUpperCase();
    }
    
    get userRoleLabel(): string {
        const translated = this.translate.instant(this.userRoleKey);
        return translated && translated !== this.userRoleKey ? translated : this.userRole;
    }
    
    logout(): void {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        this.auth.toggleLogin(false);
        this.router.navigate(['/login']);
    }

    initializeNavItems(user: any) {
        let badgeCountObservable;

        if (this.isPersonal) {
            badgeCountObservable = from(Promise.resolve({}));
        } else if (user.is_super_admin) {
            badgeCountObservable = this.api.getBadgeCountSuperadmin();
        } else if (user.user_type?.includes('Seller')) {
            badgeCountObservable = this.api.getBadgeCountSeller();
        } else {
            badgeCountObservable = this.api.getBadgeCountBuyer();
        }

        const permissionsObservable = this.isPersonal
            ? from(Promise.resolve({}))
            : this.modulePermissionService.getViewerPermissions();

        const budgetPermissionsObservable = this.isPersonal
            ? from(Promise.resolve({}))
            : this.budgetUserPermissionService.getViewersCountGroupedByModules();

        forkJoin({
            result: this.translate.get('menu'),
            permissions: permissionsObservable,
            budgetModuleUserPermissions: budgetPermissionsObservable,
            badgeCounts: badgeCountObservable
        }).subscribe(({ result, permissions, budgetModuleUserPermissions, badgeCounts }) => {
            const data: any = badgeCounts;

            if (!this.isPersonal) {
                if (user.is_super_admin) {
                    this.badgeCountEprojectClients = Number(data.clients);
                    this.badgeCountMessages = Number(data.messages);
                    this.badgeCountCompanies = Number(data.companies);
                    this.badgeCountPersonalUser = Number(data.personal);
                } else if (user.user_type === "Seller") {
                    this.badgeCountCompanyUser = Number(data.users);
                    this.badgeCountMyBuyer = Number(data.buyers);
                    this.badgeCountProduct = Number(data.products);
                    this.badgeCountInvitedRFQ = Number(data.invitedRFQs);
                    this.badgeCountPublicRFQ = Number(data.publicRFQs);
                    this.badgeCountReceivedPO = Number(data.receivedPOs);
                } else {
                    this.badgeCountCompanyUser = Number(data.users);
                    this.badgeCountMySeller = Number(data.sellers);
                    this.badgeCountCostCode = Number(data.costCodes);
                    this.badgeCountCostManager = Number(data.costManagers);
                    this.badgeCountBudget = Number(data.budgets);
                    this.badgeCountPurchaseRequest = Number(data.purchaseRequests);
                    this.badgeCountRequestForQuotation = Number(data.rfqs);
                    this.badgeCountPurchaseOrder = Number(data.purchaseOrders);
                    this.badgeCountGoodsReceiptNote = Number(data.goodsReceiptNotes);
                }
            }

            this.superAdminNavItems = [
                {
                    title: true,
                    name: result.superAdminMenu.title
                },
                {
                    name: result.superAdminMenu.eprojectClients,
                    url: '/eproject-clients',
                    iconComponent: { name: 'cil-group' },
                    badge: this.badgeCountEprojectClients > 0 ? {
                        text: this.badgeCountEprojectClients.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.superAdminMenu.announcementEmail,
                    url: '/emails/announcement',
                    iconComponent: { name: 'cil-envelope-open' }
                },
                {
                    name: result.superAdminMenu.messages,
                    url: '/conversations',
                    iconComponent: { name: 'cil-chat-bubble' },
                    badge: this.badgeCountMessages > 0 ? {
                        text: this.badgeCountMessages.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.superAdminMenu.personal,
                    url: '/personal',
                    iconComponent: { name: 'cil-user' },
                    badge: this.badgeCountPersonalUser > 0 ? {
                        text: this.badgeCountPersonalUser.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.superAdminMenu.companies,
                    url: '/companies',
                    iconComponent: { name: 'cil-home' },
                    badge: this.badgeCountCompanies > 0 ? {
                        text: this.badgeCountCompanies.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.superAdminMenu.unitOfMeasurements,
                    url: '/unit-of-measurements',
                    iconComponent: { name: 'cil-line-spacing' }
                },
                {
                    name: result.superAdminMenu.countries,
                    url: '/countries',
                    iconComponent: { name: 'cil-globe-alt' }
                },
                {
                    name: result.superAdminMenu.currencies,
                    url: '/currencies',
                    iconComponent: { name: 'cil-dollar' }
                },
                {
                    name: result.superAdminMenu.taxCodes,
                    url: '/tax-codes',
                    iconComponent: { name: 'cil-dollar' }
                },
                {
                    name: result.superAdminMenu.propertyTypes,
                    url: '/property-types',
                    iconComponent: { name: 'cil-briefcase' }
                },
                {
                    name: result.superAdminMenu.businessEntityTypes,
                    url: '/business-entity-types',
                    iconComponent: { name: 'cil-briefcase' }
                },
                {
                    name: result.superAdminMenu.costCode,
                    url: '/cost-code',
                    iconComponent: { name: 'cil-calculator' }
                },
                {
                    name: result.superAdminMenu.subscriptionPlanSetup,
                    url: '/subscription/plan/setup',
                    iconComponent: { name: 'cil-spreadsheet' }
                },
                {
                    name: result.superAdminMenu.userLogs,
                    url: '/user-logs',
                    iconComponent: { name: 'cil-user' }
                },
                {
                    name: result.superAdminMenu.settings,
                    url: '/settings',
                    iconComponent: { name: 'cil-settings' }
                },
            ];

            this.buyerAdminNavItems = [
                {
                    title: true,
                    name: result.buyerAdminMenu.title
                },
                {
                    name: result.superAdminMenu.support,
                    url: '#',
                    iconComponent: { name: 'cil-notes' },
                    children: [
                        {
                            name: result.superAdminMenu.tutorials,
                            url: '/tutorials/buyer',
                            icon: 'nav-icon-bullet'
                        },
                        {
                            name: result.superAdminMenu.submitTicket,
                            url: '/submit-ticket',
                            icon: 'nav-icon-bullet'
                        },
                    ]
                },
                {
                    name: result.buyerAdminMenu.messages,
                    url: '/conversations',
                    iconComponent: { name: 'cil-chat-bubble' },
                },
                {
                    name: result.buyerAdminMenu.companyUsers,
                    url: '/company-users',
                    iconComponent: { name: 'cil-user' },
                    badge: this.badgeCountCompanyUser > 0 ? {
                        text: this.badgeCountCompanyUser.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.buyerAdminMenu.userLogs,
                    url: '/log-activities',
                    iconComponent: { name: 'cil-user' }
                },
                {
                    name: result.buyerAdminMenu.modulePermissions,
                    url: '/module-permissions',
                    iconComponent: { name: 'cil-lock-locked' }
                },
                {
                    name: result.buyerAdminMenu.mySellers,
                    url: '/my-sellers',
                    iconComponent: { name: 'cil-group' },
                    badge: this.badgeCountMySeller > 0 ? {
                        text: this.badgeCountMySeller.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.buyerAdminMenu.marketPlace,
                    url: '/products',
                    iconComponent: { name: 'cil-calculator' }
                }
            ];

            this.sellerAdminNavItems = [
                {
                    title: true,
                    name: result.sellerAdminMenu.title,
                },
                {
                    name: result.superAdminMenu.support,
                    url: '#',
                    iconComponent: { name: 'cil-notes' },
                    children: [
                        {
                            name: result.superAdminMenu.tutorials,
                            url: '/tutorials/seller',
                            icon: 'nav-icon-bullet'
                        },
                        {
                            name: result.superAdminMenu.submitTicket,
                            url: '/submit-ticket',
                            icon: 'nav-icon-bullet'
                        },
                    ]
                },
                {
                    name: result.sellerAdminMenu.messages,
                    url: '/conversations',
                    iconComponent: { name: 'cil-chat-bubble' },
                },
                {
                    name: result.sellerAdminMenu.companyUsers,
                    url: '/company-users',
                    iconComponent: { name: 'cil-user' },
                    badge: this.badgeCountCompanyUser > 0 ? {
                        text: this.badgeCountCompanyUser.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.sellerAdminMenu.userLogs,
                    url: '/log-activities',
                    iconComponent: { name: 'cil-user' }
                },
                {
                    name: result.sellerAdminMenu.myBuyers,
                    url: '/my-clients',
                    iconComponent: { name: 'cil-user' },
                    badge: this.badgeCountMyBuyer > 0 ? {
                        text: this.badgeCountMyBuyer.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.sellerAdminMenu.myProducts,
                    url: '/my-products',
                    iconComponent: { name: 'cil-grid' },
                    badge: this.badgeCountProduct > 0 ? {
                        text: this.badgeCountProduct.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.sellerAdminMenu.invitedRFQ,
                    url: '/invited-rfq',
                    iconComponent: { name: 'cil-dollar' },
                    badge: this.badgeCountInvitedRFQ > 0 ? {
                        text: this.badgeCountInvitedRFQ.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.sellerAdminMenu.publicRFQ,
                    url: '/public-rfq',
                    iconComponent: { name: 'cil-dollar' },
                    badge: this.badgeCountPublicRFQ > 0 ? {
                        text: this.badgeCountPublicRFQ.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.sellerAdminMenu.receivedPurchaseOrder,
                    url: '/purchase-order-seller',
                    iconComponent: { name: 'cil-notes' },
                    badge: this.badgeCountReceivedPO > 0 ? {
                        text: this.badgeCountReceivedPO.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
            ];

            this.buyerNavItems = [
                {
                    title: true,
                    name: result.buyerMenu.title
                },
                {
                    name: result.superAdminMenu.support,
                    url: '#',
                    iconComponent: { name: 'cil-notes' },
                    children: [
                        {
                            name: result.superAdminMenu.tutorials,
                            url: '/tutorials/buyer',
                            icon: 'nav-icon-bullet'
                        },
                        {
                            name: result.superAdminMenu.submitTicket,
                            url: '/submit-ticket',
                            icon: 'nav-icon-bullet'
                        },
                    ]
                },
                {
                    name: result.buyerMenu.messages,
                    url: '/conversations',
                    iconComponent: { name: 'cil-chat-bubble' },
                },
                {
                    name: result.buyerAdminMenu.mySellers,
                    url: '/my-sellers',
                    iconComponent: { name: 'cil-user' },
                    badge: this.badgeCountMySeller > 0 ? {
                        text: this.badgeCountMySeller.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.buyerAdminMenu.marketPlace,
                    url: '/products',
                    iconComponent: { name: 'cil-calculator' }
                }
            ];

            this.sellerNavItems = [
                {
                    title: true,
                    name: result.sellerMenu.title
                },
                {
                    name: result.superAdminMenu.support,
                    url: '#',
                    iconComponent: { name: 'cil-notes' },
                    children: [
                        {
                            name: result.superAdminMenu.tutorials,
                            url: '/tutorials/seller',
                            icon: 'nav-icon-bullet'
                        },
                        {
                            name: result.superAdminMenu.submitTicket,
                            url: '/submit-ticket',
                            icon: 'nav-icon-bullet'
                        },
                    ]
                },
                {
                    name: result.sellerMenu.messages,
                    url: '/conversations',
                    iconComponent: { name: 'cil-chat-bubble' },
                },
                {
                    name: result.sellerAdminMenu.myBuyers,
                    url: '/my-clients',
                    iconComponent: { name: 'cil-user' },
                    badge: this.badgeCountMyBuyer > 0 ? {
                        text: this.badgeCountMyBuyer.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.sellerAdminMenu.myProducts,
                    url: '/my-products',
                    iconComponent: { name: 'cil-grid' },
                    badge: this.badgeCountProduct > 0 ? {
                        text: this.badgeCountProduct.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.sellerAdminMenu.invitedRFQ,
                    url: '/invited-rfq',
                    iconComponent: { name: 'cil-dollar' },
                    badge: this.badgeCountInvitedRFQ > 0 ? {
                        text: this.badgeCountInvitedRFQ.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.sellerAdminMenu.publicRFQ,
                    url: '/public-rfq',
                    iconComponent: { name: 'cil-dollar' },
                    badge: this.badgeCountPublicRFQ > 0 ? {
                        text: this.badgeCountPublicRFQ.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                },
                {
                    name: result.sellerAdminMenu.receivedPurchaseOrder,
                    url: '/purchase-order-seller',
                    iconComponent: { name: 'cil-notes' },
                    badge: this.badgeCountReceivedPO > 0 ? {
                        text: this.badgeCountReceivedPO.toString(),
                        color: 'primary',
                        class: 'custom-badge'
                    } : undefined
                }
            ];

            if (permissions[MODULE_PERMISSION.COST_CODE]) {
                this.buyerAdminNavItems.push(
                    {
                        name: result.buyerAdminMenu.costCode,
                        url: '/cost-code',
                        iconComponent: { name: 'cil-calculator' },
                        badge: this.badgeCountCostCode > 0 ? {
                            text: this.badgeCountCostCode.toString(),
                            color: 'primary',
                            class: 'custom-badge'
                        } : undefined
                    },
                );

                this.buyerNavItems.push(
                    {
                        name: result.buyerAdminMenu.costCode,
                        url: '/cost-code',
                        iconComponent: { name: 'cil-calculator' },
                        badge: this.badgeCountCostCode > 0 ? {
                            text: this.badgeCountCostCode.toString(),
                            color: 'primary',
                            class: 'custom-badge'
                        } : undefined
                    },
                );
            }

            if (permissions[MODULE_PERMISSION.COST_MANAGER]) {
                this.buyerAdminNavItems.push(
                    {
                        name: result.buyerAdminMenu.costManager,
                        url: '/cost-manager',
                        iconComponent: { name: 'cil-dollar' },
                        badge: this.badgeCountCostManager > 0 ? {
                            text: this.badgeCountCostManager.toString(),
                            color: 'primary',
                            class: 'custom-badge'
                        } : undefined
                    },
                );

                this.buyerNavItems.push(
                    {
                        name: result.buyerAdminMenu.costManager,
                        url: '/cost-manager',
                        iconComponent: { name: 'cil-dollar' },
                        badge: this.badgeCountCostManager > 0 ? {
                            text: this.badgeCountCostManager.toString(),
                            color: 'primary',
                            class: 'custom-badge'
                        } : undefined
                    },
                );
            }

            if (permissions[MODULE_PERMISSION.BUDGET] || budgetModuleUserPermissions.hasOwnProperty(BUDGET_MODULE_PERMISSION.BUDGET) && budgetModuleUserPermissions[BUDGET_MODULE_PERMISSION.BUDGET] > 0) {
                this.buyerAdminNavItems.push(
                    {
                        name: result.buyerAdminMenu.budgets,
                        url: '/budgets',
                        iconComponent: { name: 'cil-dollar' },
                        badge: this.badgeCountBudget > 0 ? {
                            text: this.badgeCountBudget.toString(),
                            color: 'primary',
                            class: 'custom-badge'
                        } : undefined
                    },
                );

                this.buyerNavItems.push(
                    {
                        name: result.buyerAdminMenu.budgets,
                        url: '/budgets',
                        iconComponent: { name: 'cil-dollar' },
                        badge: this.badgeCountBudget > 0 ? {
                            text: this.badgeCountBudget.toString(),
                            color: 'primary',
                            class: 'custom-badge'
                        } : undefined
                    },
                );
            }

            if (budgetModuleUserPermissions.hasOwnProperty(BUDGET_MODULE_PERMISSION.PURCHASE_REQUEST) && budgetModuleUserPermissions[BUDGET_MODULE_PERMISSION.PURCHASE_REQUEST] > 0) {
                this.buyerAdminNavItems.push(
                    {
                        name: result.buyerAdminMenu.purchaseRequest,
                        url: '/purchase-request',
                        iconComponent: { name: 'cil-notes' },
                        badge: this.badgeCountPurchaseRequest > 0 ? {
                            text: this.badgeCountPurchaseRequest.toString(),
                            color: 'primary',
                            class: 'custom-badge'
                        } : undefined
                    }
                );

                this.buyerNavItems.push(
                    {
                        name: result.buyerAdminMenu.purchaseRequest,
                        url: '/purchase-request',
                        iconComponent: { name: 'cil-notes' },
                        badge: this.badgeCountPurchaseRequest > 0 ? {
                            text: this.badgeCountPurchaseRequest.toString(),
                            color: 'primary',
                            class: 'custom-badge'
                        } : undefined
                    },
                );
            }

            if (budgetModuleUserPermissions.hasOwnProperty(BUDGET_MODULE_PERMISSION.REQUEST_FOR_QUOTATION) && budgetModuleUserPermissions[BUDGET_MODULE_PERMISSION.REQUEST_FOR_QUOTATION] > 0) {
                this.buyerAdminNavItems.push(
                    {
                        name: result.buyerAdminMenu.requestForQuotation,
                        url: '/request-for-quotation',
                        iconComponent: { name: 'cil-notes' },
                        badge: this.badgeCountRequestForQuotation > 0 ? {
                            text: this.badgeCountRequestForQuotation.toString(),
                            color: 'primary',
                            class: 'custom-badge'
                        } : undefined
                    },
                );

                this.buyerNavItems.push(
                    {
                        name: result.buyerAdminMenu.requestForQuotation,
                        url: '/request-for-quotation',
                        iconComponent: { name: 'cil-notes' },
                        badge: this.badgeCountRequestForQuotation > 0 ? {
                            text: this.badgeCountRequestForQuotation.toString(),
                            color: 'primary',
                            class: 'custom-badge'
                        } : undefined
                    },
                );
            }

            if (budgetModuleUserPermissions.hasOwnProperty(BUDGET_MODULE_PERMISSION.PURCHASE_ORDER) && budgetModuleUserPermissions[BUDGET_MODULE_PERMISSION.PURCHASE_ORDER] > 0) {
                this.buyerAdminNavItems.push(
                    {
                        name: result.buyerAdminMenu.purchaseOrder,
                        url: '/purchase-order',
                        iconComponent: { name: 'cil-notes' },
                        badge: this.badgeCountPurchaseOrder > 0 ? {
                            text: this.badgeCountPurchaseOrder.toString(),
                            color: 'primary',
                            class: 'custom-badge'
                        } : undefined
                    }
                );

                this.buyerNavItems.push(
                    {
                        name: result.buyerAdminMenu.purchaseOrder,
                        url: '/purchase-order',
                        iconComponent: { name: 'cil-notes' },
                        badge: this.badgeCountPurchaseOrder > 0 ? {
                            text: this.badgeCountPurchaseOrder.toString(),
                            color: 'primary',
                            class: 'custom-badge'
                        } : undefined
                    },
                );
            }

            if (budgetModuleUserPermissions.hasOwnProperty(BUDGET_MODULE_PERMISSION.GOODS_RECEIPT_NOTE) && budgetModuleUserPermissions[BUDGET_MODULE_PERMISSION.GOODS_RECEIPT_NOTE] > 0) {
                this.buyerAdminNavItems.push(
                    {
                        name: result.buyerAdminMenu.goodsReceiptNote,
                        url: '/goods-receipt-note',
                        iconComponent: { name: 'cil-notes' },
                        badge: this.badgeCountGoodsReceiptNote > 0 ? {
                            text: this.badgeCountGoodsReceiptNote.toString(),
                            color: 'primary',
                            class: 'custom-badge'
                        } : undefined
                    }
                );

                this.buyerNavItems.push(
                    {
                        name: result.buyerAdminMenu.goodsReceiptNote,
                        url: '/goods-receipt-note',
                        iconComponent: { name: 'cil-notes' },
                        badge: this.badgeCountGoodsReceiptNote > 0 ? {
                            text: this.badgeCountGoodsReceiptNote.toString(),
                            color: 'primary',
                            class: 'custom-badge'
                        } : undefined
                    },
                );
            }

            if (permissions[MODULE_PERMISSION.PROPERTYX]) {
                const userLogsIndex = this.sellerAdminNavItems.findIndex(
                    item => item.url === '/log-activities'
                );

                this.sellerAdminNavItems.splice(
                    userLogsIndex + 1,
                    0,
                    {
                        name: result.sellerAdminMenu.PropertyX,
                        url: '#',
                        iconComponent: { name: 'cil-home' },
                        children: [
                            {
                                name: 'Property Listings',
                                url: '/property-listings',
                                icon: 'nav-icon-bullet'
                            },
                            // {
                            //     name: 'Booking Management',
                            //     url: '/booking-management',
                            //     icon: 'nav-icon-bullet'
                            // },
                            // {
                            //     name: 'Buyer/Tenant Management',
                            //     url: '/buyer-tenant-management',
                            //     icon: 'nav-icon-bullet'
                            // },
                            // {
                            //     name: 'Payment & Billing',
                            //     url: '/payment-billing',
                            //     icon: 'nav-icon-bullet'
                            // },
                            // {
                            //     name: 'Handover Management',
                            //     url: '/handover-management',
                            //     icon: 'nav-icon-bullet'
                            // },
                            // {
                            //     name: 'Maintenance Requests',
                            //     url: '/maintenance-requests',
                            //     icon: 'nav-icon-bullet'
                            // },
                        ]
                    }
                );
            }

            // switch (user.user_type) {
            //     case "Buyer": {
            //         this.navItems = this.buyerNavItems;

            //         if (user.is_company_admin) {
            //             this.navItems = this.buyerAdminNavItems;
            //         }
            //         break;
            //     }
            //     case "Seller": {
            //         this.navItems = this.sellerNavItems;

            //         if (user.is_company_admin) {
            //             this.navItems = this.sellerAdminNavItems;
            //         }
            //         break;
            //     }
            //     default: {
            //         this.navItems = [];
            //         break;
            //     }
            // }

            if (user.user_type?.includes('Seller')) {
                this.navItems = this.sellerNavItems;
                if (user.is_company_admin) {
                    this.navItems = this.sellerAdminNavItems;
                }
            } else if (user.user_type?.includes('Buyer')) {
                this.navItems = this.buyerNavItems;
                if (user.is_company_admin) {
                    this.navItems = this.buyerAdminNavItems;
                }
            } else {
                this.navItems = [];
            }

            if (user.is_super_admin) {
                this.navItems = this.superAdminNavItems;
            }

            if (user.country_id === 'Malaysia') {
                this.sellerAdminNavItems.push(
                    {
                        name: result.sellerAdminMenu.lhdnEInvoice,
                        url: '/e-invoice',
                        iconComponent: { name: 'cil-notes' },
                    }
                );
            }
        }
        )
    }

    ngAfterContentInit(): void { }

    ngAfterViewInit(): void {
        this.initializeNavItems(this.auth.user());
        // Reinitialize the component when the route changes
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.ngOnInit();
            }
        });
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
    }

    onScrollbarUpdate($event: any) {
    }

    onMenuItemClick($event: any): void {
        setTimeout(() => {
            let clickedUrl = $event.view.location.hash.replace('#', '');
            if (clickedUrl === this.previousUrl) {
                this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                    this.router.navigate([clickedUrl]); // Navigate to the same route to reload the component
                });
            }
        }, 1000);
    }
}